
import { useState } from 'react';

interface TourHeroProps {
  title: string;
  subtitle: string;
  badge: string;
  heroImage: string;
  gallery: string[];
  rating: number;
  reviewCount: number;
  price: number;
  originalPrice: number;
}

export default function TourHero({ title, subtitle, badge, heroImage, gallery, rating, reviewCount, price, originalPrice }: TourHeroProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const allImages = [heroImage, ...gallery];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <section className="pt-24 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 h-[480px]">
            <div className="w-3/5 relative rounded-2xl overflow-hidden cursor-pointer group" onClick={() => openLightbox(0)}>
              <img src={heroImage} alt={title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              {badge && (
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-1.5 bg-[#FF6B6B] text-white text-xs font-bold rounded-full whitespace-nowrap">{badge}</span>
                </div>
              )}
              <div className="absolute bottom-8 left-8 text-white">
                <p className="text-sm text-white/80 mb-2">{subtitle}</p>
                <h1 className="text-4xl font-bold mb-3">{title}</h1>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-fill text-sm ${i < Math.floor(rating) ? 'text-[#FFB300]' : 'text-white/30'}`}></i>
                    ))}
                    <span className="ml-1 text-sm font-medium">{rating}</span>
                  </div>
                  <span className="text-sm text-white/70">({reviewCount}개 리뷰)</span>
                </div>
              </div>
            </div>

            <div className="w-2/5 grid grid-cols-2 gap-4">
              {gallery.slice(0, 4).map((img, idx) => (
                <div
                  key={idx}
                  className="relative rounded-2xl overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(idx + 1)}
                >
                  <img src={img} alt={`${title} ${idx + 1}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  {idx === 3 && gallery.length > 4 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">+{gallery.length - 3} 더보기</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span className="flex items-center gap-1.5"><i className="ri-camera-line"></i> 사진 {allImages.length}장</span>
              <span className="flex items-center gap-1.5"><i className="ri-map-pin-line"></i> 세부, 필리핀</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-400 line-through text-lg">₱{originalPrice.toLocaleString()}</span>
              <span className="text-3xl font-bold text-[#00BCD4]">₱{price.toLocaleString()}</span>
              <span className="text-sm text-gray-500">/ 1인</span>
            </div>
          </div>
        </div>
      </section>

      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center text-white text-2xl hover:bg-white/10 rounded-full transition-colors cursor-pointer"
            onClick={() => setLightboxOpen(false)}
          >
            <i className="ri-close-line"></i>
          </button>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white text-xl transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + allImages.length) % allImages.length); }}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <img
            src={allImages[lightboxIndex]}
            alt=""
            className="max-w-[85vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white text-xl transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % allImages.length); }}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
          <div className="absolute bottom-6 text-white text-sm">{lightboxIndex + 1} / {allImages.length}</div>
        </div>
      )}
    </>
  );
}
